import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { usePropertyTypes } from "@/hooks/usePropertyTypes";
import {
  useProvinces,
  useAllDistricts,
  useAllCities,
} from "@/hooks/useLocations"; // Import specific location hooks
import { useMemo, useCallback } from "react";
import { Province, District, City } from "@/lib/types"; // Import types

interface ActiveFiltersDisplayProps {
  propertyTypes: string[];
  listingTypes?: string[];
  provinceSlug: string | null; // Changed to slug
  districtSlug: string | null; // Changed to slug
  citySlug: string | null; // Changed to slug
  priceRange?: { min: number; max: number };
  sqftRange?: { min: number; max: number };
  featured?: boolean;
  onRemoveFilter: (
    type:
      | "propertyType"
      | "listingType"
      | "province"
      | "district"
      | "city"
      | "priceRange"
      | "sqftRange"
      | "featured",
    value?: string | number
  ) => void;
  onClearAll: () => void;
}

const ActiveFiltersDisplay: React.FC<ActiveFiltersDisplayProps> = ({
  propertyTypes,
  listingTypes = [],
  provinceSlug, // Changed to slug
  districtSlug, // Changed to slug
  citySlug, // Changed to slug
  priceRange,
  sqftRange,
  featured,
  onRemoveFilter,
  onClearAll,
}) => {
  const { propertyTypes: propertyTypeOptions } = usePropertyTypes();
  const { data: allProvincesData, isLoading: isLoadingProvinces } =
    useProvinces();
  const { data: allDistrictsData, isLoading: isLoadingAllDistricts } =
    useAllDistricts();
  const { data: allCitiesData, isLoading: isLoadingAllCities } = useAllCities();

  // Add console logs for debugging
  console.log("ActiveFiltersDisplay - provinceSlug:", provinceSlug);
  console.log("ActiveFiltersDisplay - districtSlug:", districtSlug);
  console.log("ActiveFiltersDisplay - citySlug:", citySlug);
  console.log(
    "ActiveFiltersDisplay - allProvincesData:",
    allProvincesData,
    "isLoadingProvinces:",
    isLoadingProvinces
  );
  console.log(
    "ActiveFiltersDisplay - allDistrictsData:",
    allDistrictsData,
    "isLoadingAllDistricts:",
    isLoadingAllDistricts
  );
  console.log(
    "ActiveFiltersDisplay - allCitiesData:",
    allCitiesData,
    "isLoadingAllCities:",
    isLoadingAllCities
  );

  // Create maps for looking up display names
  const propertyTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    propertyTypeOptions?.forEach((type: any) => {
      if (type && type.name) {
        map.set(type.name, type.display_name || type.name);
      }
    });
    return map;
  }, [propertyTypeOptions]);

  const listingTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    map.set("sale", "For Sale");
    map.set("rent", "For Rent");
    map.set("lease", "For Lease");
    return map;
  }, []);

  const getDisplayName = useCallback(
    (slug: string | null, type: "province" | "district" | "city") => {
      if (slug === null) return "";

      // Ensure data is loaded before attempting to find
      if (
        (type === "province" && isLoadingProvinces) ||
        (type === "district" && isLoadingAllDistricts) ||
        (type === "city" && isLoadingAllCities)
      ) {
        return `Loading ${type}...`; // Or return an empty string, or the slug itself
      }

      let foundItem;
      if (type === "province") {
        foundItem = allProvincesData?.find((p) => p.slug === slug);
        console.log(`getDisplayName - Province '${slug}':`, foundItem);
        return foundItem?.name || `Province: ${slug}`;
      } else if (type === "district") {
        foundItem = allDistrictsData?.find((d) => d.slug === slug);
        console.log(`getDisplayName - District '${slug}':`, foundItem);
        return foundItem?.name || `District: ${slug}`;
      } else if (type === "city") {
        foundItem = allCitiesData?.find((c) => c.slug === slug);
        console.log(`getDisplayName - City '${slug}':`, foundItem);
        return foundItem?.display_name || foundItem?.name || `City: ${slug}`;
      }
      return slug;
    },
    [
      allProvincesData,
      allDistrictsData,
      allCitiesData,
      isLoadingProvinces,
      isLoadingAllDistricts,
      isLoadingAllCities,
    ]
  );

  const activeFilters = useMemo(() => {
    const filters: {
      type: string;
      value: string | number;
      display: string;
      filterKey:
        | "propertyType"
        | "listingType"
        | "province"
        | "district"
        | "city"
        | "priceRange"
        | "sqftRange"
        | "featured";
      removeValue?: string | number;
    }[] = [];

    listingTypes.forEach((type) => {
      filters.push({
        type: "Listing Type",
        value: type,
        display: type.charAt(0).toUpperCase() + type.slice(1),
        filterKey: "listingType",
        removeValue: type,
      });
    });

    propertyTypes.forEach((type) => {
      filters.push({
        type: "Property Type",
        value: type,
        display: propertyTypeMap.get(type) || type,
        filterKey: "propertyType",
        removeValue: type,
      });
    });

    if (provinceSlug !== null) {
      filters.push({
        type: "Province",
        value: provinceSlug,
        display: getDisplayName(provinceSlug, "province"),
        filterKey: "province",
        removeValue: provinceSlug,
      });
    }

    if (districtSlug !== null) {
      filters.push({
        type: "District",
        value: districtSlug,
        display: getDisplayName(districtSlug, "district"),
        filterKey: "district",
        removeValue: districtSlug,
      });
    }

    if (citySlug !== null) {
      filters.push({
        type: "City",
        value: citySlug,
        display: getDisplayName(citySlug, "city"),
        filterKey: "city",
        removeValue: citySlug,
      });
    }

    if (priceRange && (priceRange.min > 0 || priceRange.max < 50000000)) {
      filters.push({
        type: "Price",
        value: `${priceRange.min}-${priceRange.max}`,
        display: `LKR ${priceRange.min.toLocaleString()} - ${priceRange.max.toLocaleString()}`,
        filterKey: "priceRange",
        removeValue: undefined, // Explicitly set to undefined for full clear
      });
    }

    if (sqftRange && (sqftRange.min > 0 || sqftRange.max < 10000)) {
      filters.push({
        type: "Area",
        value: `${sqftRange.min}-${sqftRange.max}`,
        display: `${sqftRange.min.toLocaleString()} - ${sqftRange.max.toLocaleString()} sqft`,
        filterKey: "sqftRange",
        removeValue: undefined, // Explicitly set to undefined for full clear
      });
    }

    if (featured) {
      filters.push({
        type: "Featured",
        value: "true",
        display: "Featured Only",
        filterKey: "featured",
        removeValue: undefined, // Explicitly set to undefined for full clear
      });
    }

    return filters;
  }, [
    listingTypes,
    propertyTypes,
    provinceSlug,
    districtSlug,
    citySlug,
    priceRange,
    sqftRange,
    featured,
    getDisplayName,
    propertyTypeMap,
  ]);

  const hasActiveFilters = activeFilters.length > 0;

  if (!hasActiveFilters) return null;

  return (
    <div className="p-4 border-b border-gray-200">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-medium">Active Filters</h3>
        <button
          onClick={onClearAll}
          className="text-xs text-propertylk-blue hover:underline"
        >
          Clear All
        </button>
      </div>
      <div className="flex flex-wrap gap-1">
        {activeFilters.map((filter, index) => (
          <Badge
            key={`${filter.filterKey}-${filter.value}-${index}`}
            variant="secondary"
            className="pl-2 pr-1 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 border-blue-200"
          >
            {filter.display}
            <button
              onClick={() =>
                onRemoveFilter(filter.filterKey, filter.removeValue)
              }
              className="ml-1 p-0.5 rounded-full hover:bg-blue-300 focus:outline-none"
              aria-label={`Remove ${filter.display} filter`}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
};

export default ActiveFiltersDisplay;
